import { gql, useQuery, useReactiveVar } from '@apollo/client';
import { CircularProgress, Popover } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip/Chip';
import type { SelectChangeEvent } from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { itemSearchQuery } from 'src/dynamoQueries/search';
import AllCategoriesDropdown from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopFilterNavBar/AllCategoriesDropdown/AllCategoriesDropdown';
import AllChoicesDropdown from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopFilterNavBar/AllChoicesDropdown/AllChoicesDropdown';
import { formatDateFilterValue, isDateField } from 'src/helpers/dateFields';
import { buildFilterChip, type FieldDisplayMeta, getExistingRangeForCategory } from 'src/helpers/filterPersistence';
import { fieldScopeToItemType, scopeToItemType } from 'src/helpers/scope';
import { processSearchParams } from 'src/helpers/searchHelper';
import viewStateVar from 'src/store/viewState';
import type { BaseSearchParams } from 'src/utils/searchSchemas';
import CommandPaletteFilter from './CommandPaletteFilter';
import useStyles from './FilterWidgetStyles';

interface FilterChip {
  choice: string;
  category: string;
  chip: string;
}

interface FacetEdge {
  key: string;
  doc_count: number;
  label?: string;
}

interface FilterWidgetProps {
  placement: 'leftSideFilter' | 'topSideFilter';
  resetFilterArgs?: () => void;
  filterAnchorEl?: HTMLElement | null;
  filterPopoverOpen?: boolean;
  filterAnchorPosition?: { top: number; left: number };
  onCloseFilterPopover?: () => void;
}

const resetState = {
  showHorizontal: false,
  horizontalMediaCardDetails: { image: '', title: '' },
  akomiMediaCard: [],
  horizontalMediaCardIndex: 0,
  pagination: { ...viewStateVar().pagination, cursor: null },
};

const FilterWidget: React.FC<FilterWidgetProps> = (props) => {
  const classes = useStyles();
  const [currentChoices, setCurrentChoices] = useState<string[]>([]);
  const [facets, setFacets] = useState<FacetEdge[] | null>(null);
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  // Get typed search params from TanStack Router - this is the source of truth
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const galleryId = (params as { galleryId?: string }).galleryId;
  const {
    filter: { category },
    filterChips,
    viewModels,
  } = useReactiveVar(viewStateVar);

  // Build slug→label map from the already-cached ViewFilterBarFirstDropdown view model
  const slugToLabelMap = useMemo(() => {
    const vm = viewModels?.library?.ViewFilterBarFirstDropdown;
    const edges = vm?.node?.viewfieldSet?.edges;
    if (!edges) return new Map<string, string>();
    return new Map<string, string>(
      edges
        .filter((e) => e.node.field?.slug)
        .map((e) => [e.node.field?.slug as string, e.node.field?.name || e.node.fieldLabel] as [string, string]),
    );
  }, [viewModels?.library?.ViewFilterBarFirstDropdown]);

  const slugToScopeMap = useMemo(() => {
    const vm = viewModels?.library?.ViewFilterBarFirstDropdown;
    const edges = vm?.node?.viewfieldSet?.edges;
    if (!edges) return new Map<string, string[] | undefined>();
    return new Map<string, string[] | undefined>(
      edges
        .filter((e) => e.node.field?.slug)
        .map((e) => [e.node.field?.slug as string, e.node.field?.scope] as [string, string[] | undefined]),
    );
  }, [viewModels?.library?.ViewFilterBarFirstDropdown]);

  const slugToFieldMetaMap = useMemo(() => {
    const vm = viewModels?.library?.ViewFilterBarFirstDropdown;
    const edges = vm?.node?.viewfieldSet?.edges;
    if (!edges) return new Map<string, FieldDisplayMeta>();
    return new Map<string, FieldDisplayMeta>(
      edges
        .filter((e) => e.node.field?.slug)
        .map((e) => [
          e.node.field?.slug as string,
          { fieldType: e.node.field?.fieldType, dateDisplayStyle: e.node.field?.dateDisplayStyle },
        ]),
    );
  }, [viewModels?.library?.ViewFilterBarFirstDropdown]);

  const selectedFieldMeta = useMemo(() => slugToFieldMetaMap.get(category.name), [slugToFieldMetaMap, category.name]);
  const isDateCategory = isDateField(selectedFieldMeta?.fieldType);

  // True once the view model has loaded, so chip labels are resolved (not raw slugs)
  const labelsReady = viewModels?.library?.ViewFilterBarFirstDropdown != null;
  const hasUrlFilters = (search.filters || []).length > 0;
  const categoryScopedItemType = fieldScopeToItemType(slugToScopeMap.get(category.name));
  const itemType = scopeToItemType(search.scope) ?? categoryScopedItemType;

  const buildFacetsQuery = (): ReturnType<typeof gql> => {
    const query = itemSearchQuery(false);
    query.query.opensearch.facets = true;
    query.query.opensearch.facetsMeta = true;

    const gQuery = jsonToGraphQLQuery(query, { pretty: true });
    return gql(gQuery);
  };

  // Track the active category so stale onCompleted callbacks are ignored
  const activeCategoryRef = useRef({ id: category.id, name: category.name });
  activeCategoryRef.current = { id: category.id, name: category.name };
  const requestCategory = { id: category.id, name: category.name };

  const { loading } = useQuery(buildFacetsQuery(), {
    fetchPolicy: 'network-only',
    skip: !category?.id,
    variables: {
      q: `${processSearchParams(search, galleryId).replace('%3A', ':')}&facets=${category.id}`,
      itemType,
    },
    onCompleted: (data: { opensearch: { facetsMeta: string } }) => {
      // Guard: ignore stale completions if category was cleared or changed
      if (
        !activeCategoryRef.current.id ||
        activeCategoryRef.current.id !== requestCategory.id ||
        activeCategoryRef.current.name !== requestCategory.name
      ) {
        return;
      }
      const parsed = JSON.parse(data.opensearch.facetsMeta);
      const facetsData = parsed[requestCategory.name];
      if (!Array.isArray(facetsData)) {
        setFacets([]);
        return;
      }
      setFacets(facetsData as FacetEdge[]);
    },
  });

  // Sync filter chips with URL params - runs on mount AND when URL filters change
  // (e.g., on browser back navigation). Also re-runs when slugToLabelMap arrives
  // so chips get proper labels once view model data is available.
  // biome-ignore lint/correctness/useExhaustiveDependencies: addFiltersFromURL reads search.filters, slugToLabelMap, and slugToFieldMetaMap internally
  useEffect(() => {
    addFiltersFromURL();
  }, [JSON.stringify(search.filters), slugToLabelMap, slugToFieldMetaMap]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: addFilter depends on currentChoices and intentionally excludes category to avoid stale closure
  useEffect(() => {
    addFilter();
  }, [currentChoices]);

  const addFiltersFromURL = (): void => {
    const choices = search.filters || [];
    const mychips = choices.map((choice) => buildFilterChip(choice, slugToLabelMap, slugToFieldMetaMap));
    const excludes = mychips.map((c) => c.category);

    viewStateVar({
      ...viewStateVar(),
      ...resetState,
      filterChips: mychips,
      filterCategoryExclude: excludes,
    });
  };

  const setChips = (chips: FilterChip[]): void => {
    viewStateVar({
      ...viewStateVar(),
      filterChips: chips,
    });
  };

  const setCategoryExclude = (ex: string[]): void => {
    viewStateVar({
      ...viewStateVar(),
      filterCategoryExclude: ex,
    });
  };

  const handleChoice = (event: SelectChangeEvent<string[]>): void => {
    const {
      target: { value },
    } = event;
    setCurrentChoices(typeof value === 'string' ? value.split(',') : value);
  };

  const handleCategory = (id: string, name: string, label: string): void => {
    setFacets(null);
    viewStateVar({
      ...viewStateVar(),
      filter: {
        ...viewStateVar().filter,
        category: { id, name, label },
      },
    });
  };

  const addFilter = (): void => {
    if (category.name && currentChoices.length !== 0) {
      const mychips: FilterChip[] = currentChoices.map((choice) => {
        const displayValue = facets?.find((f) => f.key === choice)?.label ?? choice;
        return {
          choice: choice,
          category: category.name,
          chip: `${category.label}: ${displayValue}`,
        };
      });

      const chips = [...(viewStateVar().filterChips as FilterChip[]), ...mychips];
      setCategoryExclude(chips.map((c) => c.category));
      // Update URL via TanStack Router navigate
      const newFilters = chips.map((c) => `${c.category}:${c.choice}`);
      navigate({
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          filters: newFilters,
          page: 1,
          // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
        })) as any,
      });
      setChips(chips);
      viewStateVar({
        ...viewStateVar(),
        ...resetState,
        filter: {
          category: { id: '', name: '', label: '' },
          choices: [],
        },
      });

      setCurrentChoices([]);
    }
  };

  const handleDeleteChip = (index: number): void => {
    const newChips = [...(viewStateVar().filterChips as FilterChip[])];
    newChips.splice(index, 1);
    setCategoryExclude(newChips.map((chip) => chip.category));
    setChips(newChips);
    // Update URL via TanStack Router navigate
    const newFilters = newChips.map((c) => `${c.category}:${c.choice}`);
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        filters: newFilters,
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
    viewStateVar({
      ...viewStateVar(),
      ...resetState,
      filter: { ...viewStateVar().filter, choices: [] },
    });
  };

  const resetFilters = (): void => {
    handleDeleteAllChip();
  };

  const handleDeleteAllChip = (): void => {
    setChips([]);
    // Update URL via TanStack Router navigate - clear all filters
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        filters: [],
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
    setCategoryExclude([]);
    viewStateVar({
      ...viewStateVar(),
      ...resetState,
      filter: {
        ...viewStateVar().filter,
        category: { id: '', name: '', label: '' },
        choices: [],
      },
    });
  };

  // Values already applied for the currently-selected category (shown as checked in command palette)
  const appliedValues = useMemo(() => {
    if (!category.name) return [];
    return (filterChips as FilterChip[]).filter((c) => c.category === category.name).map((c) => c.choice);
  }, [filterChips, category.name]);

  // Add a single value as a chip and return to category list
  const handleChoiceToggle = (choice: string): void => {
    const displayValue = facets?.find((f) => f.key === choice)?.label ?? choice;
    const newChip: FilterChip = {
      choice,
      category: category.name,
      chip: `${category.label}: ${displayValue}`,
    };
    const chips = [...(viewStateVar().filterChips as FilterChip[]), newChip];
    const newFilters = chips.map((c) => `${c.category}:${c.choice}`);

    // Single atomic update: add chip + reset category back to list
    viewStateVar({
      ...viewStateVar(),
      filterChips: chips,
      filterCategoryExclude: chips.map((c) => c.category),
      showHorizontal: false,
      horizontalMediaCardDetails: { image: '', title: '' },
      akomiMediaCard: [],
      horizontalMediaCardIndex: 0,
      pagination: { ...viewStateVar().pagination, cursor: null },
      filter: {
        ...viewStateVar().filter,
        category: { id: '', name: '', label: '' },
      },
    });
    setFacets(null);

    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        filters: newFilters,
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
  };

  // Remove a single value's chip from the command palette (toggle off)
  const handleChoiceRemove = (choice: string): void => {
    const newChips = (viewStateVar().filterChips as FilterChip[]).filter(
      (c) => !(c.category === category.name && c.choice === choice),
    );
    const newFilters = newChips.map((c) => `${c.category}:${c.choice}`);

    viewStateVar({
      ...viewStateVar(),
      filterChips: newChips,
      filterCategoryExclude: newChips.map((c) => c.category),
      showHorizontal: false,
      horizontalMediaCardDetails: { image: '', title: '' },
      akomiMediaCard: [],
      horizontalMediaCardIndex: 0,
      pagination: { ...viewStateVar().pagination, cursor: null },
    });

    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        filters: newFilters,
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
  };

  // Go back to category selection in the command palette
  const handleCategoryBack = (): void => {
    viewStateVar({
      ...viewStateVar(),
      filter: {
        ...viewStateVar().filter,
        category: { id: '', name: '', label: '' },
      },
    });
    setFacets(null);
  };

  const showClearFilters = useMemo(() => {
    return filterChips.length !== 0 || category.id !== '';
  }, [filterChips, category.id]);

  const handleApplyDateRange = (from: string, to: string): void => {
    const typedFilterChips = filterChips as FilterChip[];
    const otherChips = typedFilterChips.filter((c) => c.category !== category.name);
    const fromDisplay = formatDateFilterValue(from, selectedFieldMeta?.fieldType, selectedFieldMeta?.dateDisplayStyle);
    const toDisplay = formatDateFilterValue(to, selectedFieldMeta?.fieldType, selectedFieldMeta?.dateDisplayStyle);
    const rangeChip: FilterChip = {
      choice: `${from}..${to}`,
      category: category.name,
      chip: `${category.label}: ${fromDisplay} \u2013 ${toDisplay}`,
    };
    const chips = [...otherChips, rangeChip];
    const newFilters = chips.map((c) => `${c.category}:${c.choice}`);

    viewStateVar({
      ...viewStateVar(),
      filterChips: chips,
      filterCategoryExclude: chips.map((c) => c.category),
      showHorizontal: false,
      horizontalMediaCardDetails: { image: '', title: '' },
      akomiMediaCard: [],
      horizontalMediaCardIndex: 0,
      pagination: { ...viewStateVar().pagination, cursor: null },
      filter: {
        ...viewStateVar().filter,
        category: { id: '', name: '', label: '' },
      },
    });
    setFacets(null);

    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        filters: newFilters,
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
  };

  if (loading && props.placement === 'leftSideFilter') return <CircularProgress color="secondary" />;

  return (
    <>
      {props.placement === 'leftSideFilter' && (
        <>
          <div className={classes.filterChips}>
            {!labelsReady && hasUrlFilters
              ? (search.filters || []).map((_, i) => (
                  <Skeleton
                    // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder skeletons
                    key={`skeleton-${i}`}
                    variant="rounded"
                    width={120}
                    height={32}
                    className={classes.chipsItself}
                    sx={{ borderRadius: '16px' }}
                  />
                ))
              : (viewStateVar().filterChips as FilterChip[]).map((chip, index) => (
                  <Chip
                    // biome-ignore lint/suspicious/noArrayIndexKey: filter chips may have duplicate values
                    key={index}
                    className={classes.chipsItself}
                    onDelete={() => handleDeleteChip(index)}
                    label={chip.chip}
                  />
                ))}
          </div>

          <div>
            {showClearFilters && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {' '}
                <Button className={classes.clearFiltersButton} onClick={() => resetFilters()}>
                  CLEAR FILTERS
                </Button>
              </div>
            )}
          </div>
          <div className={classes.filterOptions}>
            <AllCategoriesDropdown
              category={category.id}
              handleCategory={handleCategory}
              categoryExclude={viewStateVar().filterCategoryExclude}
            />
            {category.id !== '' && facets && (
              <AllChoicesDropdown handleChoice={handleChoice} data={facets} allChoices={currentChoices} />
            )}
            {category.id !== '' && !facets && (
              <Box style={{ marginLeft: 14 }} display="flex" alignItems="center" justifyContent="center">
                <CircularProgress size={20} color="secondary" />
              </Box>
            )}
          </div>
        </>
      )}
      {props.placement === 'topSideFilter' && (
        <Popover
          open={Boolean(props.filterPopoverOpen)}
          anchorReference="anchorPosition"
          anchorPosition={props.filterAnchorPosition}
          onClose={() => {
            props.onCloseFilterPopover?.();
            handleCategoryBack();
          }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          marginThreshold={16}
          slotProps={{
            paper: {
              className: classes.filterPopover,
              elevation: 0,
            },
          }}
        >
          <CommandPaletteFilter
            selectedCategory={category}
            onSelectCategory={handleCategory}
            categoryExclude={viewStateVar().filterCategoryExclude}
            facets={facets}
            facetsLoading={loading}
            onSelectValue={handleChoiceToggle}
            onRemoveValue={handleChoiceRemove}
            appliedValues={appliedValues}
            onBack={handleCategoryBack}
            isDateCategory={isDateCategory}
            fieldType={selectedFieldMeta?.fieldType}
            dateDisplayStyle={selectedFieldMeta?.dateDisplayStyle}
            existingRange={
              category.name ? getExistingRangeForCategory(filterChips as FilterChip[], category.name) : null
            }
            onApplyRange={handleApplyDateRange}
          />
        </Popover>
      )}
    </>
  );
};

export default FilterWidget;
